/**
 * Input controller for camera (WASD/arrows + edge scroll), selection, and
 * contextual commands. Handles mouse, touch and pen through a single set of
 * Pointer Event listeners so behaviour never double-fires between real mouse
 * events and the synthetic mouse events browsers emit for touch.
 *
 * Desktop mouse semantics are unchanged: left-drag = box select / click
 * select, right-click = contextual command, scroll wheel = zoom.
 *
 * Touch semantics (see handleTap / pinch handling below):
 *   - Tap on your own unit/building selects it (double-tap selects all
 *     visible units of that type).
 *   - Tap on ground/an entity while you have a selection issues the same
 *     contextual command right-click issues on desktop (move / gather /
 *     serve / attack). Tapping empty ground with no selection is a no-op.
 *   - One-finger drag beyond a small threshold pans the camera instead of
 *     issuing a command.
 *   - Two-finger pinch zooms (and incidentally pans with the pinch midpoint).
 *
 * Delete intentionally does nothing (no delete command exists in the sim).
 */

import { BUILDINGS, dist, entityCenter } from '../sim';
import type { Entity, EntityId, UnitType } from '../sim';
import { TILE_SIZE, clampCamera, screenToWorld, setZoom, viewportTiles } from './camera';
import type { GameSession } from './session';

const EDGE_PX = 24;
const CAMERA_SPEED = 16; // tiles/sec
const TAP_MOVE_THRESHOLD = 10; // px — beyond this a touch is a drag/pan, not a tap
const DOUBLE_TAP_MS = 350;
const WHEEL_ZOOM_SENSITIVITY = 0.0015;

export interface InputCallbacks {
  onToggleScoreboard: () => void;
}

interface Point {
  x: number;
  y: number;
}

export class InputController {
  private keys = new Set<string>();
  private dragging = false;
  private mouseCss: { x: number; y: number } = { x: -1, y: -1 };
  private lastFrame = performance.now();

  // Touch bookkeeping (see class doc comment for the gesture model).
  private touchPoints = new Map<number, Point>();
  private touchPanId: number | null = null;
  private touchStart: Point = { x: 0, y: 0 };
  private lastTouchPanPos: Point | null = null;
  private touchMoved = false;
  private pinchActive = false;
  private pinchStartDist = 1;
  private pinchStartZoom = 1;
  private lastTapAt = 0;
  private lastTapEntityId: EntityId | null = null;

  constructor(
    private canvas: HTMLCanvasElement,
    private minimapCanvas: HTMLCanvasElement,
    private session: GameSession,
    private callbacks: InputCallbacks,
  ) {
    canvas.addEventListener('pointerdown', (e) => this.onPointerDown(e));
    canvas.addEventListener('pointermove', (e) => this.onPointerMove(e));
    canvas.addEventListener('pointerup', (e) => this.onPointerUp(e));
    canvas.addEventListener('pointercancel', (e) => this.onPointerCancel(e));
    canvas.addEventListener('wheel', (e) => this.onWheel(e), { passive: false });
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    window.addEventListener('keydown', (e) => this.onKeyDown(e));
    window.addEventListener('keyup', (e) => this.keys.delete(e.key.toLowerCase()));
    minimapCanvas.addEventListener('pointerdown', (e) => this.onMinimapClick(e));
    minimapCanvas.addEventListener('pointermove', (e) => {
      if (e.buttons & 1) this.onMinimapClick(e);
    });
  }

  /** Call once per animation frame to advance camera scrolling. */
  tick(): void {
    const now = performance.now();
    const dt = Math.min(0.1, (now - this.lastFrame) / 1000);
    this.lastFrame = now;
    if (!this.session.state) return;

    let dx = 0;
    let dy = 0;
    if (this.keys.has('w') || this.keys.has('arrowup')) dy -= 1;
    if (this.keys.has('s') || this.keys.has('arrowdown')) dy += 1;
    if (this.keys.has('a') || this.keys.has('arrowleft')) dx -= 1;
    if (this.keys.has('d') || this.keys.has('arrowright')) dx += 1;

    if (!this.dragging) {
      const w = this.canvas.clientWidth;
      const h = this.canvas.clientHeight;
      if (this.mouseCss.x >= 0 && this.mouseCss.x < EDGE_PX) dx -= 1;
      else if (this.mouseCss.x > w - EDGE_PX) dx += 1;
      if (this.mouseCss.y >= 0 && this.mouseCss.y < EDGE_PX) dy -= 1;
      else if (this.mouseCss.y > h - EDGE_PX) dy += 1;
    }

    if (dx !== 0 || dy !== 0) {
      const len = Math.hypot(dx, dy) || 1;
      this.session.camera.x += (dx / len) * CAMERA_SPEED * dt;
      this.session.camera.y += (dy / len) * CAMERA_SPEED * dt;
    }
    clampCamera(
      this.session.camera,
      this.session.state.map.width,
      this.session.state.map.height,
      this.canvas.clientWidth,
      this.canvas.clientHeight,
    );
  }

  private canvasPos(e: { clientX: number; clientY: number }): Point {
    const r = this.canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  // -------------------------------------------------------------------
  // Pointer event dispatch: routes to mouse-only logic (unchanged desktop
  // behaviour) or touch/pen gesture handling.
  // -------------------------------------------------------------------

  private onPointerDown(e: PointerEvent): void {
    if (e.pointerType === 'mouse') {
      this.onMouseDown(e);
      return;
    }
    this.onTouchDown(e);
  }

  private onPointerMove(e: PointerEvent): void {
    if (e.pointerType === 'mouse') {
      this.onMouseMove(e);
      return;
    }
    this.onTouchMove(e);
  }

  private onPointerUp(e: PointerEvent): void {
    if (e.pointerType === 'mouse') {
      this.onMouseUp(e);
      return;
    }
    this.onTouchUp(e);
  }

  private onPointerCancel(e: PointerEvent): void {
    if (e.pointerType === 'mouse') return;
    this.touchPoints.delete(e.pointerId);
    if (e.pointerId === this.touchPanId) {
      this.touchPanId = null;
      this.lastTouchPanPos = null;
    }
    if (this.touchPoints.size < 2) this.pinchActive = false;
  }

  // -------------------------------------------------------------------
  // Mouse (unchanged desktop behaviour)
  // -------------------------------------------------------------------

  private onMouseDown(e: PointerEvent): void {
    if (!this.session.state) return;
    const pos = this.canvasPos(e);
    if (e.button === 0) {
      if (this.session.cyberTarget) {
        this.tryCyberTarget(pos);
        return;
      }
      if (this.session.pendingOrder) {
        this.tryPendingOrder(pos);
        return;
      }
      if (this.session.placement) {
        this.commitPlacement(pos);
        return;
      }
      this.canvas.setPointerCapture(e.pointerId);
      this.dragging = true;
      this.session.dragBox = { x0: pos.x, y0: pos.y, x1: pos.x, y1: pos.y };
    } else if (e.button === 2) {
      this.handleRightClick(pos);
    }
  }

  private onMouseMove(e: PointerEvent): void {
    const r = this.canvas.getBoundingClientRect();
    this.mouseCss = { x: e.clientX - r.left, y: e.clientY - r.top };
    if (this.session.placement) {
      this.session.placement.cursor = screenToWorld(this.session.camera, this.mouseCss.x, this.mouseCss.y);
    }
    if (this.dragging && this.session.dragBox) {
      this.session.dragBox.x1 = this.mouseCss.x;
      this.session.dragBox.y1 = this.mouseCss.y;
    }
  }

  private onMouseUp(e: PointerEvent): void {
    if (e.button !== 0) return;
    if (!this.dragging || !this.session.dragBox || !this.session.state) {
      this.dragging = false;
      this.session.dragBox = null;
      return;
    }
    const box = this.session.dragBox;
    const dx = Math.abs(box.x1 - box.x0);
    const dy = Math.abs(box.y1 - box.y0);
    if (dx < 4 && dy < 4) {
      this.pointSelect(box.x0, box.y0);
    } else {
      this.boxSelect(box.x0, box.y0, box.x1, box.y1);
    }
    this.dragging = false;
    this.session.dragBox = null;
  }

  private onWheel(e: WheelEvent): void {
    if (!this.session.state) return;
    e.preventDefault();
    const pos = this.canvasPos(e);
    const factor = Math.exp(-e.deltaY * WHEEL_ZOOM_SENSITIVITY);
    setZoom(this.session.camera, this.session.camera.zoom * factor, pos.x, pos.y);
    clampCamera(
      this.session.camera,
      this.session.state.map.width,
      this.session.state.map.height,
      this.canvas.clientWidth,
      this.canvas.clientHeight,
    );
  }

  // -------------------------------------------------------------------
  // Touch / pen: tap = select-or-command, drag = pan, pinch = zoom.
  // -------------------------------------------------------------------

  private onTouchDown(e: PointerEvent): void {
    if (!this.session.state) return;
    e.preventDefault();
    this.canvas.setPointerCapture(e.pointerId);
    const pos = this.canvasPos(e);
    this.touchPoints.set(e.pointerId, pos);

    if (this.touchPoints.size === 1) {
      this.touchPanId = e.pointerId;
      this.touchStart = pos;
      this.lastTouchPanPos = pos;
      this.touchMoved = false;
      this.pinchActive = false;
    } else if (this.touchPoints.size === 2) {
      this.pinchActive = true;
      this.touchMoved = true; // a second finger joining always cancels any pending tap
      const pts = Array.from(this.touchPoints.values());
      this.pinchStartDist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
      this.pinchStartZoom = this.session.camera.zoom;
    }
  }

  private onTouchMove(e: PointerEvent): void {
    if (!this.touchPoints.has(e.pointerId) || !this.session.state) return;
    const pos = this.canvasPos(e);
    this.touchPoints.set(e.pointerId, pos);

    if (this.session.placement) {
      this.session.placement.cursor = screenToWorld(this.session.camera, pos.x, pos.y);
    }

    if (this.pinchActive && this.touchPoints.size >= 2) {
      const pts = Array.from(this.touchPoints.values()).slice(0, 2);
      const d = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
      const mid = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
      const newZoom = this.pinchStartZoom * (d / this.pinchStartDist);
      setZoom(this.session.camera, newZoom, mid.x, mid.y);
      clampCamera(
        this.session.camera,
        this.session.state.map.width,
        this.session.state.map.height,
        this.canvas.clientWidth,
        this.canvas.clientHeight,
      );
      return;
    }

    if (e.pointerId === this.touchPanId && this.lastTouchPanPos) {
      const totalDx = pos.x - this.touchStart.x;
      const totalDy = pos.y - this.touchStart.y;
      if (!this.touchMoved && Math.hypot(totalDx, totalDy) > TAP_MOVE_THRESHOLD) {
        this.touchMoved = true;
      }
      if (this.touchMoved) {
        const stepDx = pos.x - this.lastTouchPanPos.x;
        const stepDy = pos.y - this.lastTouchPanPos.y;
        const ts = TILE_SIZE * this.session.camera.zoom;
        this.session.camera.x -= stepDx / ts;
        this.session.camera.y -= stepDy / ts;
        clampCamera(
          this.session.camera,
          this.session.state.map.width,
          this.session.state.map.height,
          this.canvas.clientWidth,
          this.canvas.clientHeight,
        );
      }
      this.lastTouchPanPos = pos;
    }
  }

  private onTouchUp(e: PointerEvent): void {
    const pos = this.touchPoints.get(e.pointerId) ?? this.canvasPos(e);
    this.touchPoints.delete(e.pointerId);
    try {
      this.canvas.releasePointerCapture(e.pointerId);
    } catch {
      // no-op: capture may already have been released
    }

    if (this.pinchActive) {
      if (this.touchPoints.size < 2) {
        this.pinchActive = false;
        const remaining = Array.from(this.touchPoints.entries());
        if (remaining.length === 1) {
          // One finger still down after a pinch: resume as a pan, but don't
          // let lifting the pinch finger register as a fresh tap.
          const [remainId, remainPos] = remaining[0];
          this.touchPanId = remainId;
          this.touchStart = remainPos;
          this.lastTouchPanPos = remainPos;
          this.touchMoved = true;
        }
      }
      return;
    }

    if (e.pointerId === this.touchPanId) {
      this.touchPanId = null;
      this.lastTouchPanPos = null;
      if (!this.touchMoved) {
        this.handleTap(pos);
      }
    }
  }

  private handleTapCyber(pos: Point): boolean {
    if (!this.session.cyberTarget) return false;
    this.tryCyberTarget(pos);
    return true;
  }

  private handleTap(pos: Point): void {
    if (this.handleTapCyber(pos)) return;
    if (!this.session.state) return;
    if (this.session.pendingOrder) {
      this.tryPendingOrder(pos);
      return;
    }
    if (this.session.placement) {
      // Two-step touch placement: first tap positions the ghost so you can
      // SEE it (no hover on touch), second tap on the same tile confirms.
      const placement = this.session.placement;
      const world = screenToWorld(this.session.camera, pos.x, pos.y);
      const sameTile =
        placement.armed &&
        Math.floor(world.x) === Math.floor(placement.cursor.x) &&
        Math.floor(world.y) === Math.floor(placement.cursor.y);
      if (sameTile) {
        this.commitPlacement(pos);
      } else {
        placement.cursor = world;
        placement.armed = true;
      }
      return;
    }
    const world = screenToWorld(this.session.camera, pos.x, pos.y);
    const ownHit = this.hitOwnEntityAt(world);

    if (ownHit) {
      if (ownHit.kind === 'unit') {
        const now = performance.now();
        if (this.lastTapEntityId === ownHit.id && now - this.lastTapAt < DOUBLE_TAP_MS) {
          this.selectAllOfType(ownHit.type);
          this.lastTapEntityId = null;
          return;
        }
        this.lastTapAt = now;
        this.lastTapEntityId = ownHit.id;
      }
      this.session.selection = [ownHit.id];
      return;
    }

    if (this.session.selection.length === 0) return; // no selection: taps on ground/enemies are no-ops
    const target = this.hitAnyEntityAt(world);
    this.issueContextCommand(world, target);
  }

  private selectAllOfType(type: UnitType): void {
    const s = this.session.state;
    if (!s) return;
    const me = this.session.myPlayer;
    this.session.selection = s.entities
      .filter((e) => e.kind === 'unit' && e.owner === me && e.type === type)
      .map((e) => e.id);
  }

  // -------------------------------------------------------------------
  // Shared hit-testing / selection / commands
  // -------------------------------------------------------------------

  /** Nearest entity owned by the local player under a world point (used for click/tap selection). */
  private hitOwnEntityAt(world: { x: number; y: number }): Entity | null {
    const s = this.session.state;
    if (!s) return null;
    const me = this.session.myPlayer;
    let best: Entity | null = null;
    let bestD = Infinity;
    for (const e of s.entities) {
      if (e.owner !== me) continue;
      if (e.kind === 'unit') {
        const d = dist(world, e.pos);
        if (d < 0.6 && d < bestD) {
          bestD = d;
          best = e;
        }
      } else if (e.kind === 'building') {
        const size = BUILDINGS[e.type].size;
        if (world.x >= e.pos.x && world.x < e.pos.x + size && world.y >= e.pos.y && world.y < e.pos.y + size) {
          const d = dist(world, entityCenter(e));
          if (d < bestD) {
            bestD = d;
            best = e;
          }
        }
      }
    }
    return best;
  }

  /** Nearest entity of any owner under a world point (used for right-click / tap contextual commands). */
  private hitAnyEntityAt(world: { x: number; y: number }): Entity | null {
    const s = this.session.state;
    if (!s) return null;
    let target: Entity | null = null;
    let bestD = Infinity;
    for (const e of s.entities) {
      let r = 0.6;
      let c = e.pos;
      if (e.kind === 'building') {
        r = BUILDINGS[e.type].size / 2 + 0.3;
        c = entityCenter(e);
      } else if (e.kind === 'dataNode' || e.kind === 'market') {
        r = 1.1;
      }
      const d = dist(world, c);
      if (d <= r && d < bestD) {
        bestD = d;
        target = e;
      }
    }
    return target;
  }

  private pointSelect(sx: number, sy: number): void {
    const world = screenToWorld(this.session.camera, sx, sy);
    const best = this.hitOwnEntityAt(world);
    this.session.selection = best ? [best.id] : [];
  }

  private boxSelect(sx0: number, sy0: number, sx1: number, sy1: number): void {
    const s = this.session.state!;
    const wA = screenToWorld(this.session.camera, sx0, sy0);
    const wB = screenToWorld(this.session.camera, sx1, sy1);
    const minX = Math.min(wA.x, wB.x);
    const maxX = Math.max(wA.x, wB.x);
    const minY = Math.min(wA.y, wB.y);
    const maxY = Math.max(wA.y, wB.y);
    const me = this.session.myPlayer;
    const ids: EntityId[] = [];
    for (const e of s.entities) {
      if (e.kind !== 'unit' || e.owner !== me) continue;
      if (e.pos.x >= minX && e.pos.x <= maxX && e.pos.y >= minY && e.pos.y <= maxY) ids.push(e.id);
    }
    this.session.selection = ids;
  }

  /** Consume an armed cyber attack: if the click landed on an enemy building,
   *  issue the op; either way, disarm. */
  private tryCyberTarget(pos: Point): void {
    const ability = this.session.cyberTarget;
    this.session.cyberTarget = null;
    if (!ability) return;
    const s = this.session.state;
    if (!s) return;
    const me = this.session.myPlayer;
    const world = screenToWorld(this.session.camera, pos.x, pos.y);
    const target = this.hitAnyEntityAt(world);
    if (target && target.kind === 'building' && target.owner !== me && target.owner >= 0) {
      this.session.queueCommand({ type: ability, targetId: target.id });
    } else {
      this.session.pushToast('info', 'Cyber attack cancelled — pick an enemy building.');
    }
  }

  private handleRightClick(pos: { x: number; y: number }): void {
    const s = this.session.state;
    if (!s) return;
    if (this.session.cyberTarget) {
      this.session.cyberTarget = null;
      return;
    }
    if (this.session.pendingOrder) {
      this.session.pendingOrder = null; // cancel armed guard/patrol
      return;
    }
    if (this.session.placement) {
      this.session.placement = null;
      return;
    }
    const world = screenToWorld(this.session.camera, pos.x, pos.y);
    const target = this.hitAnyEntityAt(world);
    this.issueContextCommand(world, target);
  }

  /** Consume an armed guard/patrol order against the clicked point/entity,
   *  applied to the current selection. Guard needs one friendly target;
   *  patrol collects two map points (first click stored, second fires). */
  private tryPendingOrder(pos: Point): void {
    const order = this.session.pendingOrder;
    const s = this.session.state;
    if (!order || !s) {
      this.session.pendingOrder = null;
      return;
    }
    const me = this.session.myPlayer;
    const selectedSet = new Set(this.session.selection);
    const unitIds = s.entities
      .filter((e) => e.kind === 'unit' && e.owner === me && selectedSet.has(e.id))
      .map((e) => e.id);
    const world = screenToWorld(this.session.camera, pos.x, pos.y);

    if (unitIds.length === 0) {
      this.session.pendingOrder = null;
      return;
    }

    if (order.kind === 'guard') {
      const target = this.hitAnyEntityAt(world);
      if (target && (target.kind === 'unit' || target.kind === 'building') && target.owner === me) {
        this.session.queueCommand({ type: 'guard', unitIds, targetId: target.id });
      } else {
        this.session.pushToast('info', 'Guard cancelled — pick one of your own units or buildings.');
      }
      this.session.pendingOrder = null;
      return;
    }

    // patrol: first click sets point A, second fires the command
    if (order.a === null) {
      this.session.pendingOrder = { kind: 'patrol', a: { x: world.x, y: world.y } };
      this.session.pushToast('info', 'Patrol: now click the second point.');
      return;
    }
    this.session.queueCommand({
      type: 'patrol',
      unitIds,
      ax: order.a.x,
      ay: order.a.y,
      bx: world.x,
      by: world.y,
    });
    this.session.pendingOrder = null;
  }

  /** Issue the move/gather/serve/attack command for the current selection against a resolved target. */
  private issueContextCommand(world: { x: number; y: number }, target: Entity | null): void {
    const s = this.session.state;
    if (!s) return;
    const me = this.session.myPlayer;
    const selectedSet = new Set(this.session.selection);
    const unitIds = s.entities
      .filter((e) => e.kind === 'unit' && e.owner === me && selectedSet.has(e.id))
      .map((e) => e.id);
    if (unitIds.length === 0) return;

    if (target && target.kind === 'dataNode') {
      this.session.queueCommand({ type: 'gather', unitIds, nodeId: target.id });
    } else if (
      target &&
      target.kind === 'building' &&
      target.owner === me &&
      (target.buildTicks > 0 || target.hp < target.maxHp)
    ) {
      // own unfinished/damaged building: engineers resume construction/repair
      this.session.queueCommand({ type: 'repair', unitIds, buildingId: target.id });
    } else if (target && target.kind === 'market') {
      this.session.queueCommand({ type: 'serve', unitIds, marketId: target.id });
    } else if (target && (target.kind === 'unit' || target.kind === 'building') && target.owner !== me && target.owner !== -1) {
      this.session.queueCommand({ type: 'attack', unitIds, targetId: target.id });
    } else {
      this.session.queueCommand({ type: 'move', unitIds, x: world.x, y: world.y });
    }
  }

  private commitPlacement(pos: { x: number; y: number }): void {
    const placement = this.session.placement;
    if (!placement) return;
    const world = screenToWorld(this.session.camera, pos.x, pos.y);
    this.session.queueCommand({
      type: 'build',
      unitId: placement.engineerId,
      building: placement.building,
      x: Math.floor(world.x),
      y: Math.floor(world.y),
    });
    this.session.placement = null;
  }

  private onKeyDown(e: KeyboardEvent): void {
    const k = e.key.toLowerCase();
    if (k === 'tab') {
      e.preventDefault();
      this.callbacks.onToggleScoreboard();
      return;
    }
    if (k === 'escape') {
      if (this.session.cyberTarget) this.session.cyberTarget = null;
      else if (this.session.pendingOrder) this.session.pendingOrder = null;
      else if (this.session.placement) this.session.placement = null;
      else this.session.selection = [];
      return;
    }
    if (k === 'h') {
      const s = this.session.state;
      if (s) {
        const hq = s.entities.find(
          (e) => e.kind === 'building' && e.owner === this.session.myPlayer && e.type === 'hq',
        );
        if (hq) this.session.selection = [hq.id];
      }
      return;
    }
    // Delete intentionally does nothing: no delete command exists in the sim.
    this.keys.add(k);
  }

  private onMinimapClick(e: PointerEvent): void {
    const s = this.session.state;
    if (!s) return;
    const r = this.minimapCanvas.getBoundingClientRect();
    const mx = (e.clientX - r.left) / r.width;
    const my = (e.clientY - r.top) / r.height;
    const wx = mx * s.map.width;
    const wy = my * s.map.height;
    const { vw, vh } = viewportTiles(this.canvas.clientWidth, this.canvas.clientHeight, this.session.camera.zoom);
    this.session.camera.x = wx - vw / 2;
    this.session.camera.y = wy - vh / 2;
    clampCamera(this.session.camera, s.map.width, s.map.height, this.canvas.clientWidth, this.canvas.clientHeight);
  }
}
